import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { GetDefaultProgramTemplateQuery_defaultTemplate } from '@frontend/app/queries/types/GetDefaultProgramTemplateQuery';
import { Accordion, AccordionContent, AccordionTrigger, AccordionItem } from '@frontend/shadcn/components/ui/accordion';
import { BadgePercentIcon, GiftIcon, SackDollarIcon, VideoIcon, FileIcon, ArrowLeftIcon } from '@revfluence/fresh-icons/solid/esm';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { Button } from '@frontend/shadcn/components/ui/button';
import { addDays, format } from 'date-fns';
import { GetPublicContentGuidelineById_guideline } from '@frontend/app/queries/types/GetPublicContentGuidelineById';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Modal } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { POST_TYPE_NAME, DeliverablesIcon } from '../constants';

const getBriefPerks = (briefTemplate: GetDefaultProgramTemplateQuery_defaultTemplate) => {
  if (!briefTemplate) {
    return [];
  }
  const perks = [];
  if (briefTemplate.compensationPaymentAvailable) {
    perks.push({
      title: 'Payment',
      type: 'payment',
      amount: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(briefTemplate.compensationPaymentValue ?? 0),
      icon: <SackDollarIcon fontSize={12} />
    });
  }
  if (briefTemplate.compensationFreeProductAvailable) {
    perks.push({
      title: 'Free Product',
      type: 'freeProduct',
      details: briefTemplate.compensationFreeProductValue,
      icon: <GiftIcon fontSize={12} />
    });
  }
  if (briefTemplate.compensationCommissionAvailable) {
    perks.push({
      title: 'Commission',
      type: 'commission',
      details: briefTemplate.compensationCommissionValue,
      icon: <BadgePercentIcon />
    });
  }
  return perks;
};

export interface BriefPagePreviewProps {
  brandLogo: string;
  programName: string;
  brandName: string;
  title: string;
  description: string;
  continueButtonText?: string;
  briefTemplate: GetDefaultProgramTemplateQuery_defaultTemplate;
  onSubmit?: () => void;
  contentGuideline: GetPublicContentGuidelineById_guideline;
  isSubmitting: boolean;
  backgroundColor?: string;
  isLoading?: boolean;
  onBack: () => void;
}
export const BriefPagePreview = ({
  brandLogo,
  programName,
  brandName,
  title,
  description,
  continueButtonText = 'Continue',
  briefTemplate,
  onSubmit,
  contentGuideline,
  isSubmitting,
  backgroundColor,
  isLoading,
  onBack,
}: BriefPagePreviewProps) => {
  const [showAdditionalTerms, setShowAdditionalTerms] = useState(false);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);

  const briefPerks = getBriefPerks(briefTemplate);
  const briefIntro = briefTemplate?.introMessage || '';
  const contentRequirements = briefTemplate?.contentRequirements;
  const deliverables = contentRequirements?.contentTypes?.map((type) => ({
    type,
    name: POST_TYPE_NAME[type] || type,
  }));

  const numberOfDeliverables = (
    contentRequirements?.deliverablesDueDates?.length || contentRequirements?.deliverablesWithNoDueDates
  ) || 0;

  const mappedDueDates = contentRequirements?.deliverablesDueDates?.map((date) => format(addDays(new Date(), date), 'MM/dd/yyyy'));

  const contentUsageRights = briefTemplate?.contentUsageRightValue;

  const attachments = contentGuideline?.attachments;

  const deliverableGuidelines = contentGuideline?.guidelines?.map((guideline) => {
    return {
      title: guideline.title,
        instructions: guideline.instructions.map((instruction) => instruction?.replaceAll(guideline.title, '')),
      };
    })
    .filter((guideline) => guideline.instructions.length > 0);

  return (
    <div className="w-full h-full flex justify-center @md:p-16" style={{ background: backgroundColor }}>
      <div className="w-full max-w-screen-lg h-full overflow-y-auto p-6 bg-white inline-flex flex-col justify-start items-start gap-4 rounded-4xl">
        <div className="self-stretch inline-flex justify-start items-center">
          <div className="flex-1 inline-flex flex-col justify-start items-start">
            <div className="self-stretch inline-flex justify-center items-center gap-2">
              <img className="w-8 h-8 rounded-lg" src={brandLogo} />
              <div className="flex-1 inline-flex flex-col justify-center items-start">
                <div className="self-stretch justify-start text-xs font-medium leading-tight">{programName}</div>
                <div className="text-center justify-start text-primaryMuted text-xs font-normal leading-tight">
                  {brandName}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-start items-start gap-2">
          <div className="self-stretch flex flex-col justify-start items-start">
            <div className="self-stretch justify-start text-primary text-base font-medium leading-7">{title}</div>
            <div
              className="self-stretch justify-center text-primaryMuted text-sm font-normal leading-normal [&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="w-full flex flex-col gap-4 p-6">
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
            <Skeleton className="w-full h-10" />
          </div>
        ) : (
          <>
            {/* Intro Section */}
            <div className="flex flex-col">
              <span className="text-base font-medium">Welcome</span>
              {briefIntro && (
                <div className="text-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(briefIntro) }} />
              )}
            </div>
            <Accordion
              type="multiple"
              className="[&_h3]:mb-0 w-full border-y border-border"
              defaultValue={['hero', 'deliverables', 'contentGuidelines', 'contentUsageRights']}
            >
              {/* Perks Section */}
              <AccordionItem value="hero" className="border-b-0">
                <AccordionTrigger>
                  <span className="text-base font-medium">You'll be receiving:</span>
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-3">
                  {briefPerks.map((perk) => (
                    <div key={perk.title}>
                      <div className="flex items-center gap-3">
                        {perk.icon}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{perk.title}</div>
                          <div className="text-sm text-primaryMuted">{perk.amount || perk.details}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!briefPerks.length && (
                    <div className="text-sm text-primaryMuted">No perks available</div>
                  )}
                </AccordionContent>
              </AccordionItem>

              <Separator />

              {/* Deliverables Section */}
              {deliverables && deliverables.length > 0 && (
                <>
                  <AccordionItem value="deliverables" className="border-b-0">
                    <AccordionTrigger>
                      <span className="text-base font-medium">{numberOfDeliverables} Deliverables</span>
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3">
                      <div className="text-sm">
                        You may choose to submit <span className="font-medium">any</span> of the following content
                        types:
                      </div>
                      {deliverables.map((deliverable) => (
                        <div key={deliverable.name} className="mb-2">
                          <div className="flex items-center gap-3">
                            <DeliverablesIcon contentType={deliverable.type} />
                            <div className="flex-1">
                              <div className="text-sm">{deliverable.name}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {mappedDueDates && mappedDueDates.length > 0 && (
                        <div>
                          <span className="text-sm font-medium">Deliverables due by:</span>{' '}
                          <span className="text-sm text-primaryMuted">{mappedDueDates.join(', ')}</span>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                  <Separator />
                </>
              )}

              {/* Content Guidelines Section */}
              {(deliverableGuidelines?.length > 0 || attachments?.length > 0) && (
                <>
                  <AccordionItem value="contentGuidelines" className="border-b-0">
                    <AccordionTrigger>
                      <span className="text-base font-medium">Content Guidelines</span>
                    </AccordionTrigger>
                    <AccordionContent className="flex flex-col gap-3">
                      {deliverableGuidelines?.length > 0 && (
                        <div className="flex flex-col gap-2">
                          {deliverableGuidelines.map((guideline) => (
                            <div key={guideline.title} className="flex flex-col gap-2">
                              <div className="text-sm font-medium">{guideline.title}</div>
                              <ul className="list-disc pl-5">
                                {guideline.instructions.map((instruction, index) => (
                                  <li key={instruction || index} className="text-sm">
                                    {instruction}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                      {attachments && attachments.length > 0 && (
                        <div className="flex flex-col gap-2">
                          <div className="text-sm font-medium">Attachments:</div>
                          {attachments.map((attachment) => (
                            <a
                              key={attachment.filename}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-500 hover:underline"
                            >
                              {attachment.type?.startsWith('video/') ? <VideoIcon /> : <FileIcon />}
                              <span>{attachment.filename}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                  <Separator />
                </>
              )}

              {/* Content Usage Rights Section */}
              {contentUsageRights && (
                <AccordionItem value="contentUsageRights" className="border-b-0">
                  <AccordionTrigger>
                    <span className="text-base font-medium">Content Usage Rights</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm">{contentUsageRights}</div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={hasAgreedToTerms}
                onCheckedChange={(checked) => setHasAgreedToTerms(checked === 'indeterminate' ? false : checked)}
              />
              <span>
                By checking this checkbox, you agree to the
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowAdditionalTerms(true);
                  }}
                  className="text-foreground underline hover:text-foreground hover:underline ml-1 pl-0"
                >
                  Additional Terms
                </Button>
              </span>
            </div>

            <div className="flex items-center justify-between gap-2 mt-auto mb-10 w-full">
              <Button variant="outline" size="lg" className="w-10 p-0 @xl:w-auto @xl:px-8 gap-2" onClick={onBack}>
                <ArrowLeftIcon />
                <span className="hidden @xl:block">Back</span>
              </Button>
              <Button
                variant="primary"
                className="flex-1 @xl:flex-initial"
                onClick={onSubmit}
                disabled={!hasAgreedToTerms}
                loading={isSubmitting}
                size="lg"
              >
                {continueButtonText}
              </Button>
            </div>

            <Modal
              isOpen={showAdditionalTerms}
              onClose={() => setShowAdditionalTerms(false)}
              title="Additional Terms"
              okText="I Agree"
              onOk={() => setShowAdditionalTerms(false)}
            >
              <ul className="list-disc pl-5">
                <li>
                  All content will reflect the creator's honest views and experiences with the brand's products and
                  services
                </li>
                <li>They will at all times comply with all applicable laws, rules, regulations, and guides</li>
                <li>
                  They will not submit or post content that violates, infringes, or misappropriates any rights of any
                  third parties
                </li>
              </ul>
            </Modal>
          </>
        )}
      </div>
    </div>
  );
};
